/**
 * The request's own anatomy on the ticket detail — Step 62, the review's
 * "worst daily gap": a catalogue-born service request used to open as a
 * subject and nothing else. Shows WHICH item was requested and every stored
 * answer, labelled via the same resolved definitions and `formatFieldValue`
 * the submission's message zero used — the card and the thread can never
 * disagree. Edit is DRAFT-FIRST (Kai's Step 55 ask, scoped to answers):
 * changes stage locally, Save PATCHes through the same validate-then-write
 * path as submission, and the audit log gets per-field before→after diffs.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ClipboardList, Pencil } from 'lucide-react';
import { formatFieldValue, type ResolvedField } from '@itsm/shared';
import { ticketsApi } from './api';
import { FieldRenderer } from '../fields/FieldRenderer';
import { CatalogueIcon } from '../fields/catalogueIcons';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { qk } from '../../lib/queryClient';
import { useDirty } from '../../lib/dirty';

export function RequestDetailsCard({
  ticketId,
  catalogueItem,
  requestFields,
  values,
}: {
  ticketId: string;
  catalogueItem: { id: string; name: string; icon: string | null };
  requestFields: ResolvedField[];
  values: Record<string, unknown>;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const dirty = editing && JSON.stringify(draft) !== JSON.stringify(values);
  useDirty(dirty);

  const save = useMutation({
    mutationFn: () => ticketsApi.patchFields(ticketId, draft),
    onSuccess: (res) => {
      if (!res.ok) {
        const details = (res.errorBody as { details?: Record<string, string> } | undefined)?.details;
        if (details) setErrors(details);
        toast.error(res.error ?? 'Some answers were invalid');
        return;
      }
      toast.success('Request details updated.');
      setEditing(false);
      setErrors({});
      void queryClient.invalidateQueries({ queryKey: qk.tickets.detail(ticketId) });
    },
  });

  // View mode shows only answered fields, in form order; `content` blocks
  // never hold answers and are skipped either way.
  const answered = requestFields.filter((f) => {
    if (f.definition.type === 'content') return false;
    const v = values[f.definition.key];
    return v !== undefined && v !== null && v !== '';
  });

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-3 flex items-center gap-2">
        <ClipboardList className="h-4 w-4 text-muted-foreground" />
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Request details</p>
        <Badge variant="outline" className="gap-1.5">
          <CatalogueIcon name={catalogueItem.icon} className="h-3 w-3" />
          {catalogueItem.name}
        </Badge>
        <div className="flex-1" />
        {!editing && requestFields.length > 0 && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setDraft({ ...values });
              setErrors({});
              setEditing(true);
            }}
          >
            <Pencil /> Edit
          </Button>
        )}
      </div>

      {editing ? (
        <div className="space-y-3">
          <FieldRenderer
            fields={requestFields}
            values={draft}
            onChange={(k, v) => setDraft((prev) => ({ ...prev, [k]: v }))}
            errors={errors}
          />
          {/* A plain footer band, NOT sticky.
              It was `sticky bottom-0` to stop the Cancel/Save row landing
              beside the composer's Reply/Note tabs and reading as one merged
              toolbar (Kai, 2026-09-03) — but that was a workaround for the
              layout bug fixed in TicketDetailPage on 2026-09-06: the thread
              had no scroller, so the card overflowed onto the composer.
              With a real scroller the sticky became the defect instead — it
              pins to the bottom of the SCROLLPORT, so on a long form it
              floats over the card's own remaining fields (Kai: "the button
              floats"). The compensation goes now the cause is gone. */}
          <div className="-mx-4 -mb-4 mt-1 flex justify-end gap-2 rounded-b-lg border-t bg-muted/30 px-4 py-3">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setEditing(false);
                setErrors({});
              }}
            >
              Cancel
            </Button>
            <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
              Save answers
            </Button>
          </div>
        </div>
      ) : answered.length === 0 ? (
        <p className="text-sm text-muted-foreground">No answers were recorded on this request.</p>
      ) : (
        /* ONE FIELD PER ROW, matching the reference's requested-item card and
           — more to the point — matching the EDIT pane directly above, which
           has always been a single column because that is what `FieldRenderer`
           produces. The two-column view meant reading order changed the moment
           you pressed Edit: an answer that was second on the right became
           third down the page. Kai, 2026-09-06: "go back to the single row
           layout for service requests… and the edit pane for consistency."
           Long answers were the other half of it — a paragraph reflowed into a
           half-width column against a one-word answer beside it. */
        <dl className="space-y-2.5">
          {answered.map((f) => (
            <div key={f.definition.key} className="min-w-0">
              <dt className="flex items-center gap-1.5 text-xs text-muted-foreground">
                {f.definition.label}
                {f.audience === 'agent-only' && (
                  <span className="rounded bg-muted px-1 text-[10px]">agent-only</span>
                )}
              </dt>
              <dd className="break-words text-sm">
                {formatFieldValue(f.definition, values[f.definition.key])}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
